import { STORE_LIMITS, type StoreBurnMethod } from "./store";

/**
 * The flea market (0.98, owner decision, 23 October 2026: "make a flee market for emblems. People can design emblems and
 * put them to sale on the flee market, people can also browse the flee market on the shop section. This provides a way
 * for people to design nice emblems and earn poney, they can sell for x amount of toekns (in our case Boom, but boom is
 * just a test token ...). 10% of the proceeds sold in boom are burned, a wallet has to be signed in of course, its not
 * available to guewts. Also, players cant sell emblems they bought from rhe store even if they edit over them, it has to
 * be completely different, this is to avoid plagiarism of course").
 *
 * A player signed in with their wallet puts one of their own drawn emblems up for sale, at a price in the game's token
 * (the store's coin: `game_token`). A buyer, signed in with their own wallet, pays it with two transfers of that coin
 * from their wallet: a tenth burned (the coin's own burn, as the store's payments are; a coin without one, sent to the
 * dead address), the rest straight to the seller's wallet. The game holds nothing: the server finds both transfers in
 * the coin's own records before the emblem is the buyer's. A bought emblem is worn as a picture, as the store's are
 * (never the drawing itself: it can't be opened in the editor, changed and sold again), and a design too like a store
 * emblem or another listing is refused when it's put up for sale.
 */

/** the part of every sale burned: 10% (1,000 basis points), rounded up to the coin's smallest unit */
export const MARKET_FEE_BPS = 1_000;

/** a listing's id: "m_" and 20 hex digits (a store item's id too, as a ticket and a match carry it: 22 characters) */
export const MARKET_ID = /^m_[0-9a-f]{20}$/u;
export const isMarketId = (value: unknown): value is string => typeof value === "string" && MARKET_ID.test(value);

export const MARKET_LIMITS = Object.freeze({
	/** a listing's name */
	nameMax: 32,
	/** listings a seller has on sale at once */
	perSeller: 12,
	/** listings put up for sale in a day by one wallet, and by one network (each is drawn and compared with every other) */
	newPerDay: 24,
	newPerDayPerNetwork: 60,
	/**
	 * listings on the market at most: on sale or taken off it by their sellers (one taken down, or a banned seller's, isn't
	 * counted: 0.98 review, the market could be filled for good). Each listing's print is kept in memory for the likeness
	 * check (about 20 KB)
	 */
	kept: 2_000,
	/**
	 * listings the records keep at most, every one: the oldest taken down that nobody bought is forgotten to make room for
	 * a new one (2,500 prints, some 50 MB at most)
	 */
	keptRecords: 2_500,
	/** a listing's copies (none: no limit) */
	maxStock: 10_000,
	/**
	 * the least a price can be, in the coin's smallest units (a tenth of it, rounded up, burned: at least one unit each
	 * way). A coin with decimals asks a hundredth of a token at least (marketMinPrice): 0.98 review, sales and payments at
	 * ten units cost their makers nothing but fees
	 */
	minPriceAtomic: 10n,
	/** the most a price can be, in whole tokens (the store's) */
	maxPriceWhole: STORE_LIMITS.maxPriceWhole,
	/** payments a wallet has waiting at once, and how long one waits for its two transfers */
	maxOpenPayments: 3,
	paymentWaitMs: 30 * 60_000,
	/** a payment whose page said a transaction waits for it this long after its time at most */
	txResolveMs: 60 * 60_000,
	/** a payment given up is still paid by its two transfers for a week (the seller has their part: the buyer gets theirs) */
	latePaymentMs: 7 * 24 * 60 * 60_000,
	/** payments waiting at once, every wallet's together, and given-up ones kept */
	maxWaitingPayments: 20_000,
	maxExpiredKept: 5_000,
	/** listings the menu reads at a time */
	pageSize: 48,
	/** the least of the emblem's circle a design must cover (a dot is not an emblem to sell) */
	minInk: 0.03,
});

/** the least a listing's price can be in a coin of these decimals: a hundredth of a token (ten units at the least) */
export function marketMinPrice(decimals: number): bigint {
	const hundredth = Number.isInteger(decimals) && decimals >= 2 ? 10n ** BigInt(decimals - 2) : 1n;
	return hundredth > MARKET_LIMITS.minPriceAtomic ? hundredth : MARKET_LIMITS.minPriceAtomic;
}

/**
 * A price split as it's paid: the part burned (10%, rounded up to the coin's smallest unit) and the seller's (the rest).
 * Both are at least one unit for any price the market takes (MARKET_LIMITS.minPriceAtomic)
 */
export function marketSplit(priceAtomic: bigint): { readonly burnAtomic: bigint; readonly sellerAtomic: bigint } {
	const burnAtomic = (priceAtomic * BigInt(MARKET_FEE_BPS) + 9_999n) / 10_000n;
	return { burnAtomic, sellerAtomic: priceAtomic - burnAtomic };
}

/** where a listing's picture is, on the game server (what a match and the menu draw) */
export const marketPictureUrl = (id: string): string => `/api/market/pictures/${id}.png`;

/** a listing as everyone may see it (the market, the matches' players, the Control Room) */
export interface MarketListingView {
	readonly id: string;
	readonly name: string;
	/** the seller's wallet (lowercase: shown shortened) and the name they play under */
	readonly seller: string;
	readonly sellerName?: string;
	/** its price, and how it's paid: the part burned and the seller's (the coin's smallest units, decimal strings) */
	readonly priceAtomic: string;
	readonly burnAtomic: string;
	readonly sellerAtomic: string;
	readonly onSale: boolean;
	/** copies left to buy (none said: no limit) */
	readonly left?: number;
	readonly sold: number;
	/** its picture: /api/market/pictures/<id>.png */
	readonly url: string;
	readonly createdAt: number;
	/** the seller took it off the market (its buyers keep it, and wear it) */
	readonly withdrawn?: boolean;
	/** the owner took it down: off the market, and worn by nobody any more */
	readonly removed?: boolean;
}

export type MarketSort = "new" | "popular";

/** the market's answer to the menu: whether it's open, the coin, and a page of the listings on sale */
export interface MarketPublicView {
	readonly open: boolean;
	/** why it takes no listings or payments now, even open (no coin set up) */
	readonly problem?: string;
	readonly token?: {
		readonly address: string;
		readonly chainId: number;
		readonly decimals: number;
		readonly symbol: string;
	};
	/** how the burned part is paid now: the coin's own burn, or a transfer to the dead address (none said: not known yet) */
	readonly burnMethod?: StoreBurnMethod;
	/** the part of a sale burned (basis points) */
	readonly feeBps: number;
	/** listings on sale, every page's */
	readonly total: number;
	readonly offset: number;
	readonly sort: MarketSort;
	readonly listings: readonly MarketListingView[];
}

/** the two transfers a buyer's page is told to send, in the game's coin: the burned part, then the seller's */
export interface MarketPayInstruction {
	readonly token: string;
	readonly chainId: number;
	readonly decimals: number;
	readonly symbol: string;
	/** the coin's own burn of the amount ("burn"), or a transfer of it to `to` (the dead address) */
	readonly burn: { readonly method: StoreBurnMethod; readonly to: string; readonly amountAtomic: string };
	/** a transfer of the amount to the seller's wallet */
	readonly seller: { readonly to: string; readonly amountAtomic: string };
}

export type MarketLegName = "burn" | "seller";
export const MARKET_LEGS: readonly MarketLegName[] = Object.freeze(["burn", "seller"]);

/** one of a payment's two transfers, as its buyer sees it */
export interface MarketLegView {
	readonly amountAtomic: string;
	/** found in the coin's records: this part is paid */
	readonly paid: boolean;
	/** the transaction that paid it, or the one its page said (looked up) */
	readonly tx?: string;
}

/** a payment of a wallet, as it sees it (and the Control Room) */
export interface MarketPaymentView {
	readonly id: string;
	readonly listingId: string;
	readonly seller: string;
	readonly priceAtomic: string;
	readonly state: "waiting" | "paid" | "expired";
	readonly createdAt: number;
	readonly expiresAt: number;
	readonly legs: { readonly burn: MarketLegView; readonly seller: MarketLegView };
	/** why it's still waiting, or why a transaction sent for it wasn't taken */
	readonly note?: string;
}
